import React, { useRef, useEffect, useState } from "react";
import { motion, useInView, useMotionValue, useSpring, animate } from "framer-motion";

export const Reveal = ({ children, delay = 0, y = 28, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, delay, ease: [0.19, 1, 0.22, 1] }}
  >
    {children}
  </motion.div>
);

export const MaskLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.19, 1, 0.22, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const SectionLabel = ({ children, dark = false }) => (
  <div className="flex items-center gap-3 mb-6">
    <span className="h-[6px] w-[6px] rounded-full" style={{ background: "var(--gold)" }} />
    <span className={`text-xs font-medium uppercase tracking-[0.25em] ${dark ? "text-white/60" : "text-neutral-500"}`}>
      {children}
    </span>
  </div>
);

export const Counter = ({ to, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.8, ease: [0.19, 1, 0.22, 1],
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to]);
  return <span ref={ref}>{val}{suffix}</span>;
};

export const Magnetic = ({ children, className = "" }) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 200, damping: 15 });
  const sy = useSpring(y, { stiffness: 200, damping: 15 });
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - (r.left + r.width / 2)) * 0.3);
    y.set((e.clientY - (r.top + r.height / 2)) * 0.3);
  };
  const reset = () => { x.set(0); y.set(0); };
  return (
    <motion.div ref={ref} onMouseMove={onMove} onMouseLeave={reset} style={{ x: sx, y: sy }} className={className}>
      {children}
    </motion.div>
  );
};

export const Marquee = ({ items, reverse = false, dur = "42s", render, className = "" }) => (
  <div className={`marquee-pause overflow-hidden ${className}`}>
    <div className={`marquee-track ${reverse ? "reverse" : ""}`} style={{ "--dur": dur }}>
      {[...items, ...items].map((it, i) => (
        <div key={i} className="shrink-0">{render(it, i)}</div>
      ))}
    </div>
  </div>
);
